import React, { useState, useEffect } from 'react';
import styled from 'astroturf/react';
import pluralize from 'pluralize';
import { Modal as ModalBase } from 'js/components/modals/Modal';
import { TextButton } from 'js/design_system/TextButton';
import CloseIcon from 'js/design_system/icons/close.svg';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { FollowableUser } from 'js/social/shared/types';
import BulkFollowListModal from './BulkFollowListModal';
import { BulkFollowPreview } from './BulkFollowPreview';
import { recordShow, recordDismissal } from 'js/utils/dismissibles';
import { getFollowingSource } from './bulk_follow_modal_helper';
import { followableUserToSuggestion } from 'js/social/shared/utils';

const DISMISSIBLE_NAME = 'SwpBulkFollowTakeover';

const Modal = styled(ModalBase)`
  padding: 24px 24px 64px;
  max-width: 90%;

  @include for-tablet-up {
    padding: 24px 112px 40px;
  }
`;

const CloseButton = styled(TextButton)`
  height: 24px;
  width: 24px;
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 0;
`;

const MoreText = styled.a`
  color: $ds-primary-blue-active;
  font-weight: 500;
  cursor: pointer;
`;

export type SwpBulkFollowTakeoverProps = {
  topic: {
    name: string;
    score: number;
  };
  users: FollowableUser[];
  onClose: (followingsAdded?: boolean) => void;
};

// This component lives here because it shares a lot of the same logic with the big cards.
// If we close the test to experimental, we should consider moving it somewhere closer to SWP.
export const SwpBulkFollowTakeover: React.FC<SwpBulkFollowTakeoverProps> = ({
  topic,
  users,
  onClose,
}) => {
  const [showBulkFollowListModal, setShowBulkFollowListModal] = useState(false);
  const header = (
    <>
      Follow <span style={{ fontWeight: 500 }}>{topic.name}</span> academics
    </>
  );
  const description = (
    <>
      Follow{' '}
      {users
        .slice(0, 3)
        .map((user) => user.displayName)
        .join(', ')}
      , plus{' '}
      <MoreText
        role="button"
        onClick={() => {
          setShowBulkFollowListModal(true);
        }}
      >
        {pluralize('more academic', users.length - 3, true)}
      </MoreText>
    </>
  );

  useEffect(() => {
    recordShow(DISMISSIBLE_NAME);
  }, []);

  const handleClose = (followingsAdded?: boolean) => {
    recordDismissal(DISMISSIBLE_NAME);
    onClose(followingsAdded);
  };

  return (
    <ImpressionWatcher source={'swp_bulk_follow_takeover'}>
      {showBulkFollowListModal ? (
        <BulkFollowListModal
          open={showBulkFollowListModal}
          initialFollowSuggestions={users.map(followableUserToSuggestion)}
          cardType="swp_takeover"
          onClose={(followingsAdded) => {
            setShowBulkFollowListModal(false);
            handleClose(followingsAdded);
          }}
          topic={topic}
        />
      ) : (
        <Modal onClose={handleClose}>
          <CloseButton
            aria-label="Close"
            onClick={() => {
              handleClose();
            }}
          >
            <CloseIcon color="#91919e" />
          </CloseButton>

          <BulkFollowPreview
            topic={topic}
            header={header}
            description={description}
            users={users}
            followingSource={getFollowingSource('swp_takeover')}
            onComplete={() => {
              handleClose(true);
            }}
          />
        </Modal>
      )}
    </ImpressionWatcher>
  );
};
